// eslint-disable-next-line eslint-comments/disable-enable-pair
/* eslint-disable no-console */
/* eslint-disable jsx-a11y/label-has-associated-control */
import React, { useMemo, useState } from 'react';
import { CardNumber, Cvv, ExpiryDate, Frames, FrameValidationChangedEvent } from 'frames-react';
import { useTranslation } from 'react-i18next';
import { logError } from 'components/LogError';
import { Country, getISO2Country } from 'utils';
import { useCreateCheckoutDotComPayment } from 'views/Accounts/hooks/useCreateCheckoutDotComPayment';
import { ViewStateType } from 'components/ProductTemplates/types';
import { cache } from 'graphql/cache';
import { useGetCheckoutDotComCards } from 'views/Accounts/hooks/useGetCheckoutDotComCards';
import { FeedbackTemplate } from 'views/shared/FeedbackTemplate';
import { selectCheckoutDotComCards, userHasValidCards } from 'views/Accounts/utils';
import { Button } from 'components';
import { DropdownItem } from 'components/Dropdown';
import { CardDetailResponse, CardPaymentInput } from '__generated__/graphql';
import { COUNTRIES } from '../../../../constants';
import Visa from '../../../../assets/images/visa.jpg';
import Mastercard from '../../../../assets/images/mastercard.jpg';
import { ACCEPTED_PAYMENT_METHODS } from '../../constants';
import { CardDetailsFormProps, FlowType } from './CardDetailsForm.types';

type FrameInput = {
  'name-on-card': boolean | undefined;
  cvv: boolean | undefined;
  'card-number': boolean | undefined;
  'expiry-date': boolean | undefined;
  'poste-code': boolean | undefined;
};

export default function CardDetailsFormFrames({
  amount,
  ContainerClassName,
  flow,
  portfolioId,
  btnText,
  onSuccessUrl,
  onFailureUrl,
  isUseExistingCard = false,
  transactionType = '',
}: CardDetailsFormProps) {
  const { t } = useTranslation();
  const [makeCardDefault, setMakeCardDefault] = React.useState(true);
  const [selectedSavedCard, setSelectedSavedCard] = useState<DropdownItem>();
  const [saveCard, setSaveCard] = React.useState(true);
  const [nameOnCard, setNameOnCard] = React.useState<string>('');
  const [postcode, setPostcode] = React.useState<string>('');
  const [country, setCountry] = React.useState<Country>('United Kingdom');
  const [viewState, setViewState] = React.useState<ViewStateType>(ViewStateType.DEFAULT);
  const [isProcessing, setIsProcessing] = useState(false);
  const [isPaymentMethodValid, setIsPaymentMethodValid] = useState<null | false | true>(null);
  const { data: cardsData, error: failedToGetCards, loading: loadingCards } = useGetCheckoutDotComCards();

  const _cards = useMemo(() => {
    if (!cardsData) return [];
    return selectCheckoutDotComCards(cardsData);
  }, [cardsData]);

  const { createCardPayment, loading: addingCard } = useCreateCheckoutDotComPayment();
  const [cardDetailsValidity, setCardDetailsValidity] = React.useState<FrameInput>({
    'card-number': undefined,
    'expiry-date': undefined,
    'name-on-card': undefined,
    'poste-code': undefined,
    cvv: undefined,
  });

  const onHandleSumbit = () => {
    setViewState(ViewStateType.LOADING);
    handleSubmit();
  };

  const cardsOnFile = useMemo(() => {
    if (!_cards) return [];
    const cards = _cards.filter((card) =>
      ACCEPTED_PAYMENT_METHODS.find((x) => x.toLocaleLowerCase() === card.scheme?.toLocaleLowerCase() || ''),
    );
    const saveCards = cards.map((x) => ({
      id: x.instrumentId as string,
      value: x.instrumentId as string,
      content: x.defaultMitFlag,
      text: `${x.clientName}......${x.last4}.....${x.scheme}`,
    }));

    setSelectedSavedCard(saveCards.find((x) => x.content) ?? saveCards[0]);
    return saveCards;
  }, [_cards]);

  const handleSubmit = async () => {
    setIsProcessing(true);
    try {
      const baseReturnUri = window.location.origin + window.location.pathname;
      const successUrl = `${baseReturnUri}${
        onSuccessUrl || (flow === FlowType.TOP_UP ? '#topUp_success' : '#addCard_success')
      }`;

      const failureUrl = `${baseReturnUri}${
        onFailureUrl || (flow === FlowType.TOP_UP ? '#topUp_failure' : '#addCard_failure')
      }`;

      let request: CardPaymentInput = {
        amount,
        failureUrl,
        successUrl,
        transactionType,
      };

      if (cardsOnFile && isUseExistingCard) {
        const { instrumentId } = _cards.find((x) => x.instrumentId === selectedSavedCard?.value) as CardDetailResponse;
        request = {
          ...request,
          instrumentId,
          portfolioId: flow === FlowType.TOP_UP ? portfolioId : undefined,
        };
      } else {
        const tokenEvent = await Frames.submitCard();
        request = {
          ...request,
          cardToken: tokenEvent.token,
          clientName: nameOnCard,
          storeCardFlag: saveCard || flow === FlowType.ADD_CARD,
          mitConsentedFlag: saveCard || flow === FlowType.ADD_CARD, // if user consents to store card, then they are also opting into MIT (confirmed by HHW).
          defaultMitFlag: flow === FlowType.ADD_CARD ? makeCardDefault : false, // Only set card to Default when adding a new card. (not topping up)
          portfolioId: flow === FlowType.TOP_UP ? portfolioId : undefined,
        };
      }

      const { errors, data } = await createCardPayment({
        variables: {
          args: {
            ...request,
          },
        },
      });

      if (errors) {
        console.error(errors);
        setViewState(ViewStateType.ERROR_RESULT);
        logError(JSON.stringify(errors[0]), { tags: { userFlow: 'payment' } });
        return;
      }

      if (data?.createCardPayment.status === 'Declined') {
        console.error('Card declined');
        logError(new Error(`Card declined: ${data?.createCardPayment.errorMessage}`), {
          tags: { userFlow: 'payment' },
        });
        setViewState(ViewStateType.ERROR_RESULT);
        return;
      }

      if (data?.createCardPayment.status === 'Failed') {
        console.error('Process card failed');
        logError(new Error(`Process card failed': ${data?.createCardPayment.errorMessage}`), {
          tags: { userFlow: 'payment' },
        });
        setViewState(ViewStateType.ERROR_RESULT);
        return;
      }

      if (data?.createCardPayment.status === 'Pending') {
        const { redirectUrl } = data.createCardPayment;
        if (!redirectUrl) {
          const err = new Error('Redirect error is null');
          logError(err, { tags: { userFlow: 'payment' } });
          throw err;
        }

        cache.evict({ fieldName: 'paymentGetCardDetails' });
        window.location.href = redirectUrl;
      }
    } catch (e) {
      logError(e as Error, { tags: { userFlow: 'payment' } });
      console.error(e);
      setViewState(ViewStateType.ERROR_RESULT);
    } finally {
      setIsProcessing(false);
    }
  };

  React.useEffect(() => {
    if (failedToGetCards && flow === FlowType.ADD_CARD) {
      setViewState(ViewStateType.ERROR_RESULT);
    }
  }, [failedToGetCards, flow]);

  const validCardAlreadyOnFile = userHasValidCards(_cards);

  function _frameValidationChangedHandler(e: FrameValidationChangedEvent) {
    setCardDetailsValidity((prev) => {
      return { ...prev, [e.element as keyof typeof cardDetailsValidity]: e.isValid };
    });
  }

  function _onChangeTextHandler(text: string) {
    setNameOnCard(text);
    const nameValid = text.length >= 2 && text.length <= 24;
    setCardDetailsValidity((prev) => {
      return { ...prev, 'name-on-card': nameValid };
    });
  }

  function _onChangePostTextHandler(text: string) {
    setPostcode(text);
    const postCodeValid = text.length >= 2 && text.length <= 24;
    setCardDetailsValidity((prev) => {
      return { ...prev, 'poste-code': postCodeValid };
    });
  }

  const isDarkMode = typeof document !== 'undefined' && document.documentElement.classList.contains('dark');

  return (
    <div
      className={
        ContainerClassName ||
        'flex flex-col flex-1 bg-gray-50 dark:bg-slate-900 pb-5 px-4 w-full relative overflow-x-hidden sm:w-[360px]'
      }
    >
      <div className="w-full h-full overflow-y-auto flex flex-col">
        <form className="h-full">
          <FeedbackTemplate
            onClose={() => setViewState(ViewStateType.DEFAULT)}
            onCTA={() => setViewState(ViewStateType.DEFAULT)}
            viewState={viewState}
          >
            <div className="flex flex-col bg-white dark:bg-slate-800 rounded-2xl h-full w-full p-5 shadow-sm dark:shadow-slate-900/50 border border-gray-100 dark:border-slate-700">
              {(!cardsOnFile || !isUseExistingCard) && (
                <>
                  {/* Accepted Cards Banner */}
                  <div className="flex items-center justify-between p-3 bg-gray-50 dark:bg-slate-700/50 rounded-xl mb-5">
                    <span className="text-sm font-medium text-gray-600 dark:text-gray-300">
                      {t('account:payments.addCard.acceptedCards')}
                    </span>
                    <div className="flex gap-2">
                      <img src={Visa} alt="visa" className="h-6 rounded" />
                      <img src={Mastercard} alt="mastercard" className="h-6 rounded" />
                    </div>
                  </div>

                  <Frames
                    paymentMethodChanged={(e) => {
                      if (ACCEPTED_PAYMENT_METHODS.includes(e.paymentMethod)) {
                        setIsPaymentMethodValid(true);
                      } else {
                        setIsPaymentMethodValid(false);
                      }
                    }}
                    config={{
                      acceptedPaymentMethods: ACCEPTED_PAYMENT_METHODS,
                      debug: process.env.NODE_ENV === 'development',
                      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                      publicKey: process.env.REACT_APP_CHECKOUT_COM_PUBLIC_KEY!,
                      cardholder: {
                        billingAddress: { country: getISO2Country(country), zip: postcode },
                        name: nameOnCard,
                      },
                      localization: {
                        cardNumberPlaceholder: t('account:payments.addCard.formFields.placeholders.cardNumber'),
                        expiryMonthPlaceholder: t('account:payments.addCard.formFields.placeholders.expiryMonth'),
                        expiryYearPlaceholder: t('account:payments.addCard.formFields.placeholders.expiryYear'),
                        cvvPlaceholder: t('account:payments.addCard.formFields.placeholders.cvv'),
                      },
                      style: {
                        base: {
                          color: isDarkMode ? '#f8fafc' : '#1f2937',
                          fontSize: '14px',
                          fontWeight: '400',
                          letterSpacing: 'normal',
                          border: isDarkMode ? '1px solid #475569' : '1px solid #d1d5db',
                          borderRadius: '8px',
                          height: '47px',
                          padding: '14px 12px',
                          background: isDarkMode ? '#334155' : '#ffffff',
                          fontFamily: 'inherit',
                        },
                        placeholder: {
                          base: {
                            color: isDarkMode ? '#94a3b8' : '#9ca3af',
                          },
                        },
                        focus: {
                          border: isDarkMode ? '1px solid #14b8a6' : '1px solid #f97316',
                        },
                        invalid: {
                          border: '1px solid #ef4444',
                        },
                      },
                    }}
                    frameValidationChanged={_frameValidationChangedHandler}
                    cardValidationChanged={() => {}}
                    cardTokenized={() => {}}
                  >
                    <div className="flex flex-col gap-4">
                      {/* Name on Card */}
                      <div>
                        <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1.5">
                          {t('account:payments.addCard.formFields.labels.name', 'Name on Card')}
                        </label>
                        <input
                          id="name"
                          className="checkout-com-text-field w-full"
                          type="text"
                          placeholder={t('account:payments.addCard.formFields.placeholders.name')}
                          value={nameOnCard}
                          onChange={(e) => _onChangeTextHandler(e.target.value)}
                        />
                        {typeof cardDetailsValidity['name-on-card'] === 'boolean' &&
                          cardDetailsValidity['name-on-card'] === false && (
                            <span className="text-red-500 text-xs mt-1 block">
                              {t('account:payments.validation.nameOnCard')}
                            </span>
                          )}
                      </div>

                      {/* Card Number */}
                      <div>
                        <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1.5">
                          {t('account:payments.addCard.formFields.labels.cardNumber', 'Card Number')}
                        </label>
                        <CardNumber className="h-[47px] rounded-lg" />
                        {typeof cardDetailsValidity['card-number'] === 'boolean' &&
                          cardDetailsValidity['card-number'] === false && (
                            <span className="text-red-500 text-xs mt-1 block">
                              {t('account:payments.validation.cardNumber')}
                            </span>
                          )}
                        {typeof cardDetailsValidity['card-number'] === 'boolean' &&
                          cardDetailsValidity['card-number'] === true &&
                          isPaymentMethodValid === false && (
                            <span className="text-red-500 text-xs mt-1 block">
                              {t('account:payments.validation.acceptedCards')}
                            </span>
                          )}
                      </div>

                      {/* Expiry & CVV Row */}
                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1.5">
                            {t('account:payments.addCard.formFields.labels.expiry', 'Expiry Date')}
                          </label>
                          <ExpiryDate className="h-[47px] rounded-lg" />
                          {typeof cardDetailsValidity['expiry-date'] === 'boolean' &&
                            cardDetailsValidity['expiry-date'] === false && (
                              <span className="text-red-500 text-xs mt-1 block">
                                {t('account:payments.validation.expDate')}
                              </span>
                            )}
                        </div>
                        <div>
                          <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1.5">
                            {t('account:payments.addCard.formFields.labels.cvv', 'CVV')}
                          </label>
                          <Cvv className="h-[47px] rounded-lg" />
                          {typeof cardDetailsValidity.cvv === 'boolean' && cardDetailsValidity.cvv === false && (
                            <span className="text-red-500 text-xs mt-1 block">
                              {t('account:payments.validation.cvv')}
                            </span>
                          )}
                        </div>
                      </div>

                      {/* Postcode */}
                      <div>
                        <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1.5">
                          {t('account:payments.addCard.formFields.labels.postcode', 'Postcode / Zip')}
                        </label>
                        <input
                          id="postcode"
                          className="checkout-com-text-field w-full"
                          type="text"
                          placeholder={t('account:payments.addCard.formFields.placeholders.postcode')}
                          value={postcode}
                          onChange={(e) => _onChangePostTextHandler(e.target.value)}
                        />
                        {typeof cardDetailsValidity['poste-code'] === 'boolean' &&
                          cardDetailsValidity['poste-code'] === false && (
                            <span className="text-red-500 text-xs mt-1 block">
                              {t('account:payments.validation.postCode')}
                            </span>
                          )}
                      </div>

                      {/* Country */}
                      <div>
                        <label
                          htmlFor="country"
                          className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1.5"
                        >
                          {t('account:payments.addCard.formFields.placeholders.country')}
                        </label>
                        <select
                          id="country"
                          className="w-full text-sm outline-none bg-white dark:bg-slate-700 text-gray-900 dark:text-gray-100 border border-gray-300 dark:border-slate-600 rounded-lg px-3 py-3 focus:border-orange dark:focus:border-teal-500 transition-colors"
                          value={country}
                          onChange={({ target: { value } }) => setCountry(value)}
                        >
                          {COUNTRIES.map((option) => (
                            <option key={option} value={option}>
                              {option}
                            </option>
                          ))}
                        </select>
                      </div>

                      {/* Save Card Checkbox */}
                      {(flow === FlowType.CHARGE_ADD_CARD || flow === FlowType.TOP_UP) && (
                        <label
                          htmlFor="saveCardInput"
                          className="flex items-center gap-3 p-3 bg-gray-50 dark:bg-slate-700/50 rounded-xl cursor-pointer"
                        >
                          <input
                            id="saveCardInput"
                            type="checkbox"
                            checked={saveCard}
                            onChange={() => setSaveCard(!saveCard)}
                            className="appearance-none w-4 h-4 rounded border border-gray-300 dark:border-slate-600 bg-white dark:bg-slate-800 checked:bg-orange dark:checked:bg-teal-500 checked:border-orange dark:checked:border-teal-500 focus:ring-2 focus:ring-orange/30 dark:focus:ring-teal-500/30 focus:ring-offset-0 transition-all cursor-pointer"
                          />
                          <span className="text-sm text-gray-700 dark:text-gray-300">
                            {t('account:payments.addCard.actions.saveCardText')}
                          </span>
                        </label>
                      )}

                      {/* Make Default Checkbox */}
                      {flow === FlowType.ADD_CARD && (
                        <label
                          htmlFor="makedefault"
                          className="flex items-center gap-3 p-3 bg-gray-50 dark:bg-slate-700/50 rounded-xl cursor-pointer"
                        >
                          <input
                            id="makedefault"
                            type="checkbox"
                            checked={makeCardDefault}
                            disabled={!validCardAlreadyOnFile}
                            onChange={() => setMakeCardDefault(!makeCardDefault)}
                            className="appearance-none w-4 h-4 rounded border border-gray-300 dark:border-slate-600 bg-white dark:bg-slate-800 checked:bg-orange dark:checked:bg-teal-500 checked:border-orange dark:checked:border-teal-500 focus:ring-2 focus:ring-orange/30 dark:focus:ring-teal-500/30 focus:ring-offset-0 disabled:opacity-50 transition-all cursor-pointer"
                          />
                          <span className="text-sm text-gray-700 dark:text-gray-300">
                            {t('account:payments.addCard.formFields.makeDefault')}
                          </span>
                        </label>
                      )}
                    </div>
                  </Frames>

                  {/* Submit Button */}
                  <Button
                    isDisable={
                      isProcessing ||
                      loadingCards ||
                      addingCard ||
                      nameOnCard.length < 5 ||
                      postcode.length < 4 ||
                      country.length === 0 ||
                      isPaymentMethodValid === false
                    }
                    onClick={handleSubmit}
                    isProcessing={isProcessing}
                    type="button"
                    className={`w-full mt-6 rounded-xl px-6 py-3 font-medium transition-all flex items-center justify-center gap-2 ${
                      isProcessing ||
                      loadingCards ||
                      addingCard ||
                      nameOnCard.length < 5 ||
                      postcode.length < 4 ||
                      country.length === 0 ||
                      isPaymentMethodValid === false
                        ? 'bg-gray-200 dark:bg-slate-700 text-gray-400 dark:text-gray-500 cursor-not-allowed'
                        : 'bg-gradient-to-r from-orange to-orange/90 dark:from-teal-500 dark:to-teal-600 text-white shadow-lg shadow-orange/25 dark:shadow-teal-500/25 hover:shadow-xl'
                    }`}
                  >
                    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
                      />
                    </svg>
                    {!!btnText && btnText}
                    {!btnText &&
                      (flow === FlowType.TOP_UP
                        ? t('account:slideout.topup.title')
                        : t('account:payments.addCard.actions.save'))}
                  </Button>
                </>
              )}
              {cardsOnFile && isUseExistingCard && (
                <>
                  {/* Saved Card Selection */}
                  <div className="mb-4">
                    <label
                      htmlFor="savedCard"
                      className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1.5"
                    >
                      {t('account:payments.addCard.cardOnFile', 'Card On File')}
                    </label>
                    <select
                      id="savedCard"
                      className="w-full text-sm outline-none bg-white dark:bg-slate-700 text-gray-900 dark:text-gray-100 border border-gray-300 dark:border-slate-600 rounded-lg px-3 py-3 focus:border-orange dark:focus:border-teal-500 transition-colors"
                      value={selectedSavedCard?.value}
                      onChange={({ target: { value } }) =>
                        setSelectedSavedCard(cardsOnFile.find((x) => value === x.value))
                      }
                    >
                      {cardsOnFile.map((option) => (
                        <option key={option.id} value={option.value as string}>
                          {option.text}
                        </option>
                      ))}
                    </select>
                  </div>

                  {/* Submit Button */}
                  <Button
                    isDisable={loadingCards}
                    onClick={onHandleSumbit}
                    type="button"
                    className={`w-full mt-4 rounded-xl px-6 py-3 font-medium transition-all flex items-center justify-center gap-2 ${
                      loadingCards
                        ? 'bg-gray-200 dark:bg-slate-700 text-gray-400 dark:text-gray-500 cursor-not-allowed'
                        : 'bg-gradient-to-r from-orange to-orange/90 dark:from-teal-500 dark:to-teal-600 text-white shadow-lg shadow-orange/25 dark:shadow-teal-500/25 hover:shadow-xl'
                    }`}
                  >
                    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
                      />
                    </svg>
                    {!!btnText && btnText}
                    {!btnText &&
                      (flow === FlowType.TOP_UP
                        ? t('account:slideout.topup.title')
                        : t('account:payments.addCard.actions.save'))}
                  </Button>
                </>
              )}
            </div>
          </FeedbackTemplate>
        </form>
      </div>
    </div>
  );
}
